import { PayloadAction } from '@reduxjs/toolkit'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { fetchAllProjectDocumentMappings, restoreProjectDocumentChunks } from '../../../api/projects-api'
import { DocumentMapping } from '../../../models/documentMapping'
import {
    deleteDocumentChunks,
    NormalizedDocumentChunk,
    restoreDocumentChunks,
    selectDocumentMappings,
    selectNormalizedDocumentChunks,
    updateDocumentMappings,
} from '../../slices/documents'
import { selectProjectId } from '../../slices/project'

export function* handleRestoreDocumentChunks(action: PayloadAction<{ documentChunksIdsToRestore: number[] }>) {
    const { documentChunksIdsToRestore } = action.payload
    const projectId: number | null = yield select(selectProjectId)
    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)
    const documentChunks: NormalizedDocumentChunk[] | null = yield select(selectNormalizedDocumentChunks)

    if (!projectId) return

    try {
        yield call(restoreProjectDocumentChunks, projectId, documentChunksIdsToRestore)
    } catch (error) {
        yield put(
            deleteDocumentChunks({
                documentChunksIdsToDelete: documentChunksIdsToRestore,
            })
        )
        yield call(console.error, error)
    } finally {
        const chunks: NormalizedDocumentChunk[] = documentChunks ?? []
        const mappings: DocumentMapping[] = documentMappings ?? []
        const chunksById = new Map(chunks.map((c) => [c.id, c] as const))
        const mappingsById = new Map(mappings.map((m) => [m.id, m] as const))

        const shouldRefetchMappings = documentChunksIdsToRestore.some((chunkId) => {
            const chunk = chunksById.get(chunkId)

            return !chunk || chunk.mappingID == null || !mappingsById.has(chunk.mappingID)
        })

        if (shouldRefetchMappings) {
            try {
                const fetchedMappings: DocumentMapping[] = yield call(fetchAllProjectDocumentMappings, projectId)

                yield put(updateDocumentMappings(fetchedMappings))
            } catch (error) {
                yield call(console.error, error)
            }
        }
    }
}

export function* watchForDocumentChunksRestore() {
    yield takeEvery(restoreDocumentChunks.type, handleRestoreDocumentChunks)
}
